import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgManager from '../services/OrgManager';
import { useTranslation } from 'react-i18next';

const ROLE_KEYS = {
  owner: 'app.auth.role_owner',
  admin: 'app.auth.role_admin',
  member: 'app.auth.role_member',
  viewer: 'app.auth.role_viewer',
};

export default function AcceptInviteScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshOrgs, switchOrg } = useOrg();

  const token = searchParams.get('token') || '';
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setError(t('app.auth.invite_invalid')); setLoading(false); return; }
    OrgManager.previewInvitation(token)
      .then(data => { setPreview(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  const accept = async () => {
    setActing(true);
    setError('');
    try {
      const result = await OrgManager.acceptInvitation(token);
      await refreshOrgs();
      await switchOrg(result.org_id);
      navigate('/');
    } catch (e) {
      setError(e.message);
      setActing(false);
    }
  };

  const decline = async () => {
    try {
      await OrgManager.declineInvitation(token);
      navigate('/');
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <View style={[styles.card, { backgroundColor: theme.cardBg, borderColor: theme.border }]}>
        {loading ? (
          <Text style={{ color: theme.textSecondary }}>{t('app.auth.invite_loading')}</Text>
        ) : error ? (
          <>
            <Text style={[styles.errorTitle, { color: '#ef4444' }]}>{t('app.auth.invite_unavailable')}</Text>
            <Text style={{ color: theme.textSecondary, textAlign: 'center', marginTop: 8 }}>{error}</Text>
            <TouchableOpacity onPress={() => navigate('/')} style={[styles.btn, { backgroundColor: theme.accent, marginTop: 20 }]}>
              <Text style={styles.btnText}>{t('app.auth.invite_go_dashboard')}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={[styles.headline, { color: theme.text }]}>
              {t('app.auth.invite_headline')}
            </Text>
            <View style={[styles.orgCard, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <Text style={[styles.orgName, { color: theme.text }]}>{preview.org_name}</Text>
              <Text style={[styles.roleLine, { color: theme.textSecondary }]}>
                {t('app.auth.invite_role')}{' '}<Text style={{ color: theme.text, fontWeight: '600' }}>
                  {ROLE_KEYS[preview.role] ? t(ROLE_KEYS[preview.role]) : preview.role}
                </Text>
              </Text>
              {preview.invited_by_username && (
                <Text style={[styles.invitedBy, { color: theme.textSecondary }]}>
                  {t('app.auth.invite_invited_by', { username: preview.invited_by_username })}
                </Text>
              )}
            </View>

            {error ? <Text style={{ color: '#ef4444', marginTop: 12, fontSize: 13 }}>{error}</Text> : null}

            <View style={styles.actions}>
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: theme.accent }]}
                onPress={accept}
                disabled={acting}
              >
                <Text style={styles.btnText}>{acting ? t('app.auth.invite_joining') : t('app.auth.invite_join')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: 'transparent', borderWidth: 1, borderColor: theme.border }]}
                onPress={decline}
                disabled={acting}
              >
                <Text style={[styles.btnText, { color: theme.textSecondary }]}>{t('app.auth.invite_decline')}</Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: {
    borderWidth: 1, borderRadius: 16, padding: 32,
    maxWidth: 440, width: '100%', alignItems: 'center',
  },
  headline: { fontSize: 22, fontWeight: '700', marginBottom: 20, textAlign: 'center' },
  errorTitle: { fontSize: 18, fontWeight: '700' },
  orgCard: {
    borderWidth: 1, borderRadius: 12, padding: 18, width: '100%',
    alignItems: 'center', gap: 6,
  },
  orgName: { fontSize: 18, fontWeight: '700' },
  roleLine: { fontSize: 13 },
  invitedBy: { fontSize: 12, marginTop: 2 },
  actions: { gap: 10, marginTop: 24, width: '100%' },
  btn: { paddingVertical: 13, borderRadius: 10, alignItems: 'center' },
  btnText: { fontWeight: '600', fontSize: 14, color: '#fff' },
});
